import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputBase,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  styled,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaRegFilePdf } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import Icon from '@/components/Icon'
import { useCreateProfileMutation } from '@/store/apiService'
import { getCountriesApi } from '@/utils/appApi'

interface UserDetails {
  last_name: string
  name: string
  country: string
  vat: string
  serial_number: string
  source: string
}

const initialData = {
  name: '',
  last_name: '',
  country: '',
  vat: '',
  serial_number: '',
  source: '',
}

const errorMessages = {
  name: 'Please enter your first name.',
  country: 'Please select country',
  last_name: 'Please enter your last name.',
  source: 'Please select source',
}

const TERMS_DOCS = {
  fr: {
    html: '/conditions_generales.html',
    pdf: '/conditions_generales.pdf',
  },
  default: {
    html: '/terms_conditions.html',
    pdf: '/terms_conditions.pdf',
  },
}

const DPA_DOCS = {
  fr: {
    html: '/accord_traitement_donnees.html',
    pdf: '/accord_traitement_donnees.pdf',
  },
  default: {
    html: '/data_agreement.html',
    pdf: '/data_agreement.pdf',
  },
}

// ... (styled components skipped, will be preserved by tool if outside range)

// We need to skip to the component implementation
// Since replace_file_content works on contiguous blocks, I might need two calls if they are far apart.
// initialData is at line 33. handleChange is at line 168.
// I'll do initialData first.

export const StyledTextField = styled(TextField)(() => ({
  borderRadius: '4px',
  marginBottom: '16px',
  '& .MuiFormHelperText-root': {
    fontSize: '12px',
    color: 'red !important',
    margin: '0 2px',
  },
  '& .MuiFormLabel-root': {
    color: '#ffffff70',
    // background: '#292929',
    padding: '0 5px',
    fontSize: '16px',
  },
  '& .Mui-focused': {
    color: 'white',
  },
  '& label.Mui-focused': {
    color: 'white',
  },
  '& .MuiOutlinedInput-root': {
    fontSize: '16px',
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.23)',
      top: 0,
      borderWidth: '2px',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.7)',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.7)',
    },
    '& input': {
      '&:-webkit-autofill': {
        boxShadow: '0 0 0 1000px rgb(30,31,33) inset',
        transition: 'background-color 5000s ease-in-out 0s',
      },
    },
  },
  // "& .MuiOutlinedInput-input:-webkit-autofill": {
  //   backgroundColor: 'blue !important'
  // }
}))

const BootstrapInput = styled(InputBase)(() => ({
  root: {
    '& .MuiInputLabel-root': {
      border: '2px solid red',
    },
  },
  '& .MuiInputBase-input': {
    padding: '14px 14px',
    color: 'rgba(255, 255, 255, 1)',
    borderRadius: 4,
    marginBottom: 6,
    height: '24px',
    border: '1px solid rgba(255, 255, 255, 0.23)',
    fontSize: 16,
    '&:focus': {
      borderRadius: 4,
      borderColor: 'rgba(255, 255, 255, 1)',
    },
  },
  '& svg': {
    fill: 'white',
  },
}))

const ProfileLandingForm = ({ onLogout }: { onLogout: () => void }) => {
  const [userInfo, setUserInfo] = useState<UserDetails>(initialData)
  const [checked, setChecked] = useState<boolean>(false)
  const [errors, setErrors] = useState<any>({})
  const [countries, setCountries] = useState<any[]>([])
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const languageCode = (i18n.language || 'en').toLowerCase().split('-')[0]
  const termsDoc = languageCode === 'fr' ? TERMS_DOCS.fr : TERMS_DOCS.default
  const dpaDoc = languageCode === 'fr' ? DPA_DOCS.fr : DPA_DOCS.default

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const data = await getCountriesApi()
        setCountries(data)
      } catch (err) {
        console.error('Failed to fetch countries:', err)
      }
    }

    fetchCountries()
  }, [])

  const [createProfile] = useCreateProfileMutation()

  const isFormValid = useMemo(
    () =>
      Boolean(
        userInfo.name && userInfo.country && userInfo.last_name && checked,
      ),
    [userInfo, checked],
  )

  const isFormValidHandler = () => {
    if (!userInfo.name) {
      setErrors((prev: any) => ({
        ...prev,
        name: errorMessages['name'],
      }))
    }
    if (!userInfo.country) {
      setErrors((prev: any) => ({
        ...prev,
        country: errorMessages['country'],
      }))
    }
    if (!userInfo.source) {
      setErrors((prev: any) => ({
        ...prev,
        source: (errorMessages as any)['source'],
      }))
    }

    return !!isFormValid
  }

  const handleChange = (event: any) => {
    setUserInfo((prev) => ({
      ...prev,
      [event.target.name]: ['name', 'vat', 'serial_number'].includes(
        event.target.name,
      )
        ? event.target.value.trimStart()
        : event.target.value,
    }))
    if (event.target.value) {
      setErrors((prev: any) => ({ ...prev, [event.target.name]: '' }))
    } else {
      setErrors((prev: any) => ({
        ...prev,
        [event.target.name]: (errorMessages as any)[event.target.name],
      }))
    }
  }

  const handleContinue = () => {
    if (isFormValidHandler()) {
      const payload: any = {
        ...userInfo,
        name: `${userInfo.name.trim()} ${userInfo.last_name.trim()}`.trim(),
        vat: userInfo?.vat.trim(),
        serial_number: userInfo?.serial_number.trim(),
        signup_state: 'registered',
      }

      if (userInfo.source) {
        payload.source = userInfo.source
      }

      createProfile(payload)
        .unwrap()
        .then(() => {
          toast.success('Profile saved successfully.')
          navigate('/get-started/plans')
        })
        .catch(() => {
          toast.error('Something went wrong!')
        })
    }
  }
  return (
    <>
      <Typography
        style={{
          fontSize: '14px',
          fontWeight: 500,
          justifyContent: 'center',
          display: 'flex',
        }}
      >
        Please fill in your information.
      </Typography>
      <Box mx="25px" my="25px" width={'100%'}>
        <Box display="flex" gap={2}>
          <StyledTextField
            label="First name"
            variant="outlined"
            size="medium"
            name="name"
            value={userInfo.name}
            fullWidth
            onChange={handleChange}
            inputProps={{
              style: {
                color: 'white',
                height: '54px',
                padding: '0 10px',
              },
            }}
            helperText={errors.name && errorMessages.name}
          />

          <StyledTextField
            label="Last name"
            variant="outlined"
            size="medium"
            name="last_name"
            value={userInfo.last_name}
            fullWidth
            onChange={handleChange}
            inputProps={{
              style: {
                color: 'white',
                height: '54px',
                padding: '0 10px',
              },
            }}
            helperText={errors.name && errorMessages.name}
          />
        </Box>
        <FormControl variant="outlined" fullWidth>
          <InputLabel
            htmlFor="outlined-country-label"
            style={{
              background: 'rgb(30,31,33)',
              padding: '0 5px',
              color: userInfo.country ? 'rgba(255, 255, 255,1)' : '#ffffff70',
              fontSize: '16px',
            }}
          >
            Country
          </InputLabel>
          <Select
            sx={{
              marginBottom: '4px',
              color: '#ffffff70',
              fontSize: '16px',
              '& .MuiInputBase-input': {
                borderColor: 'rgba(255, 255, 255, 0.23)',
                borderWidth: '2px',
              },
            }}
            inputProps={{
              id: 'outlined-country-label',
            }}
            value={userInfo.country}
            name="country"
            fullWidth
            variant="outlined"
            input={<BootstrapInput />}
            onChange={handleChange}
            data-testid="country-select"
          >
            <MenuItem disabled value="Select Country" sx={{ fontSize: '16px' }}>
              <em>Select Country</em>
            </MenuItem>
            {countries?.map((country: any) => (
              <MenuItem
                sx={{ fontSize: '16px' }}
                key={country.id}
                value={Number.parseInt(country.id, 10)}
              >
                {country.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {errors.country && (
          <div style={{ color: 'red' }}>
            {(errorMessages as any)['country']}
          </div>
        )}
        <FormControl variant="outlined" fullWidth>
          <InputLabel
            htmlFor="outlined-source-label"
            style={{
              background: 'rgb(30,31,33)',
              padding: '0 5px',
              color: userInfo.source ? 'rgba(255, 255, 255,1)' : '#ffffff70',
              fontSize: '16px',
            }}
          >
            How did you find out about Matisse Software ?
          </InputLabel>
          <Select
            sx={{
              marginBottom: '4px',
              color: '#ffffff70',
              fontSize: '16px',
              '& .MuiInputBase-input': {
                borderColor: 'rgba(255, 255, 255, 0.23)',
                borderWidth: '2px',
              },
            }}
            inputProps={{
              id: 'outlined-source',
            }}
            value={userInfo.source}
            name="source"
            fullWidth
            variant="outlined"
            input={<BootstrapInput />}
            onChange={handleChange}
            data-testid="source-select"
          >
            <MenuItem disabled value="" sx={{ fontSize: '16px' }}>
              <em>Please select</em>
            </MenuItem>
            {[
              ['social', 'Social media'],
              ['smile-line', 'Smile line dealer'],
              ['matisse', 'Matisse course/lecture'],
              ['other', 'Friend/colleague'],
            ].map((source: any) => (
              <MenuItem
                key={source[0]}
                value={source[0]}
                sx={{ fontSize: '16px' }}
              >
                {source[1]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {errors.source && (
          <div style={{ color: 'red' }}>{(errorMessages as any)['source']}</div>
        )}
        <FormControlLabel
          control={
            <Checkbox
              checked={checked}
              sx={{
                color: '#90CAF9',
                '& .MuiSvgIcon-root': {
                  height: '25px',
                  width: '25px',
                },
              }}
              onChange={(event) => setChecked(event.target.checked)}
            />
          }
          label={
            <Typography sx={{ fontSize: '16px' }}>
              {t('legalAcceptance.beforeTerms')}{' '}
              <a
                href={`${window.location.origin}${encodeURI(termsDoc.html)}`}
                rel="noreferrer"
                style={{
                  margin: '0 0.3rem',
                  color: '#32ADE6',
                  textDecoration: 'none',
                }}
                className="text-decoration-none"
                target="_blank"
              >
                {t('legalAcceptance.terms')}
              </a>
              <Tooltip title={t('legalAcceptance.download')} arrow>
                <a href={encodeURI(termsDoc.pdf)} download>
                  <Icon
                    icon={FaRegFilePdf}
                    size={15}
                    style={{ cursor: 'pointer', verticalAlign: 'middle' }}
                  />
                </a>
              </Tooltip>{' '}
              {t('legalAcceptance.between')}{' '}
              <a
                href={`${window.location.origin}${encodeURI(dpaDoc.html)}`}
                className="text-decoration-none"
                target="_blank"
                rel="noreferrer"
                style={{ color: '#32ADE6', textDecoration: 'none' }}
              >
                {t('legalAcceptance.dpa')}
              </a>{' '}
              <Tooltip title={t('legalAcceptance.download')} arrow>
                <a href={encodeURI(dpaDoc.pdf)} download>
                  <Icon
                    icon={FaRegFilePdf}
                    size={15}
                    style={{ cursor: 'pointer', verticalAlign: 'middle' }}
                  />
                </a>
              </Tooltip>
              {' .'}
            </Typography>
          }
        />
        <Box display="flex" justifyContent="space-between" gap={2}>
          <Button
            fullWidth
            variant="outlined"
            onClick={onLogout}
            sx={{
              fontSize: '15px',
              marginTop: '35px',
              color: '#90CAF9',
              borderColor: '#90CAF9',
              '& :hover': {
                borderColor: '#90CAF9',
              },
              flex: 1,
              py: 1,
              maxHeight: '42px',
            }}
            startIcon={<ChevronLeftIcon />}
          >
            Go back
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleContinue}
            sx={{
              fontSize: '15px',
              color: isFormValid ? 'black' : 'gray',
              background: isFormValid ? '#90CAF9' : '#00000050',
              marginTop: '35px',
              '& :hover': {
                borderColor: '#90CAF9',
              },
              '&.Mui-disabled': {
                backgroundColor: 'rgba(255, 255, 255, 0.12)',
                color: 'rgba(255, 255, 255, 0.30)',
              },
              flex: 1,
              py: 1,
              maxHeight: '42px',
            }}
            disabled={!isFormValid}
            endIcon={<ChevronRightIcon />}
          >
            Continue
          </Button>
        </Box>
      </Box>
    </>
  )
}

export default ProfileLandingForm
